import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  AlertTypes,
  ConfirmationClaimType,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { ResetFormDataAction, UpdateFormDataAction } from '../../../actions/form-submission.action';
import { ConfirmationService } from '../../../services/confirmation-service/confirmation-service';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { InternalTransferRequest } from '../../../services/savings-account-service/savings-account.type';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { ApiActions } from '../../../state/api-loading.state';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { redirectToPage } from '../cta-button-actions.utils';
import { handleRedirection } from '../cta-redirection.utils';
import { RefreshActiveAccountDetails } from '../../../actions/savings-account.action';

export function initiateSavingsInternalTransfer(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const store = injector.get(Store);
  const confirmationService = injector.get(ConfirmationService);
  const isSmsAuthEnabled = store.selectSnapshot(
    ProjectSettingsState.getProjectSettings,
  ).smsAuthenticationEnabled;
  if (isSmsAuthEnabled) {
    const loadingAction = ApiActions.savingsInternalTransfer;
    const subscription = confirmationService.checkConfirmation(undefined, loadingAction).subscribe(() => {
      callInternalTransferAPI(injector, attri);
      subscription.unsubscribe();
    });
    confirmationService.confirmAction(ConfirmationClaimType.MAKE_PAYMENT, undefined, true, loadingAction);
  } else {
    callInternalTransferAPI(injector, attri);
  }
}

function callInternalTransferAPI(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const savingsAccountService = injector.get(SavingsAccountService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const store = injector.get(Store);
  const savingsAccountState = store.selectSnapshot(SavingsAccountState.getAccounts);
  const senderSavingsAccountDetails = store.selectSnapshot(
    SavingsAccountState.getCurrentAccountDetails(savingsAccountState?.internalTransferFromAccountId ?? ''),
  );
  const ctaAction = attri.widgetProperties.buttonActionType as string;

  const allPageFormValues = workflowFormsService.getAllFormValuesFlattened();
  const currentPageFormValues = allPageFormValues[FormID.INTERNAL_TRANSFER_FORM];
  if (!currentPageFormValues) {
    console.error('Internal transfer form data not found');
    alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Internal transfer form data not found');
    return;
  }
  // return if any of the required fields are missing
  if (
    !savingsAccountState?.internalTransferFromAccountId ||
    !savingsAccountState?.internalTransferToAccountId
  ) {
    console.error('Internal transfer from or to account id or amount not found');
    alertHandlerService.showAlertFn(
      AlertTypes.ERROR,
      'Internal transfer from or to account id or amount not found',
    );
    return;
  }

  if (!currentPageFormValues['amount']) {
    console.error('Internal transfer amount not found');
    alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Internal transfer amount not found');
    return;
  }

  const amount = Number(currentPageFormValues['amount']);
  const senderAvailableBalance = Number(senderSavingsAccountDetails?.availableBalance);

  // 1. Check for missing or invalid numerical inputs
  if (Number.isNaN(amount) || Number.isNaN(senderAvailableBalance)) {
    return alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Internal transfer amount not found');
  }

  // 2. Check for zero amount
  if (amount === 0) {
    return alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Please enter valid amount');
  }

  // 3. Check for insufficient funds
  if (amount > senderAvailableBalance) {
    return alertHandlerService.showAlertFn(
      AlertTypes.WARNING,
      'Entered amount is more than available balance',
    );
  }

  // disable button on click
  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  const payload: InternalTransferRequest = {
    amount: amount,
    accountId: savingsAccountState.internalTransferToAccountId,
  };
  savingsAccountService
    .initiateInternalTransfer(savingsAccountState.internalTransferFromAccountId, payload)
    .pipe(
      tap(() => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        store.dispatch(new ResetFormDataAction()); // reset the form state to remove stored amount
        store.dispatch(
          new UpdateFormDataAction({
            formData: {
              internalTransferStatus: 'completed',
            },
          }),
        );
        setTimeout(() => {
          store.dispatch(
            new RefreshActiveAccountDetails(savingsAccountState.internalTransferFromAccountId ?? ''),
          );
          store.dispatch(
            new RefreshActiveAccountDetails(savingsAccountState.internalTransferToAccountId ?? ''),
          );
        }, 3000); // Wait for the transaction to complete from provider's end then call the API to get fresh data
        if (attri.widgetProperties?.actionNavigationConfigurations) {
          handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
        } else {
          redirectToPage(injector.get(Router), attri.widgetProperties, injector);
        }
      }),
      catchError((error) => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        console.error('Error initiating internal transfer', error);
        store.dispatch(
          new UpdateFormDataAction({
            formData: {
              internalTransferStatus: 'failed',
            },
          }),
        );
        if (attri.widgetProperties?.actionNavigationConfigurations) {
          handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
        } else {
          redirectToPage(injector.get(Router), attri.widgetProperties, injector);
        }
        throw error;
      }),
    )
    .subscribe();
}
